import { PMR171_COMMAND } from './radioSerialProtocol';
import { parseFactoryCommandTypeCode } from './pmr171FactoryCalibrationMqtt';
import {
  ANALOG_CTCSS_MAX,
  CHANNEL_FREQUENCY_MAX,
  CHANNEL_INDEX_MAX,
  CHANNEL_INDEX_MIN,
  CHANNEL_VFO_MODES,
  encodeAnalogChannelWrite,
  encodeChannelName,
  parseChannelName,
  type AnalogChannelParams,
} from '../pages/radioRemote/serialDebug/serialChannel';

export type Pmr171ChannelEvent =
  | {
      kind: 'analog';
      deviceId: string;
      type: string;
      typeCode: number;
      timestamp: number;
      channel: AnalogChannelParams;
    }
  | {
      kind: 'mode';
      deviceId: string;
      type: string;
      typeCode: number;
      timestamp: number;
      mode: 0 | 1;
    };

export function channelTypeHex(typeCode: number): string {
  return `0X${typeCode.toString(16).toUpperCase().padStart(2, '0')}`;
}

export function isPmr171ChannelType(type: string): boolean {
  const code = parseFactoryCommandTypeCode(type);
  if (code == null) return false;
  return (
    code === PMR171_COMMAND.CHANNEL_WRITE
    || code === PMR171_COMMAND.CHANNEL_READ
    || code === PMR171_COMMAND.CHANNEL_MODE
  );
}

function readFiniteNumber(data: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim())) return Number(value.trim());
  }
  return null;
}

function readIntInRange(
  data: Record<string, unknown>,
  min: number,
  max: number,
  ...keys: string[]
): number | null {
  const value = readFiniteNumber(data, ...keys);
  if (value == null) return null;
  const rounded = Math.round(value);
  if (rounded < min || rounded > max) return null;
  return rounded;
}

function parseAnalogFromData(data: Record<string, unknown>): AnalogChannelParams | null {
  const channel = readIntInRange(data, CHANNEL_INDEX_MIN, CHANNEL_INDEX_MAX, 'channel');
  const vfoAMode = readIntInRange(data, 0, CHANNEL_VFO_MODES.length - 1, 'vfoAMode');
  const vfoBMode = readIntInRange(data, 0, CHANNEL_VFO_MODES.length - 1, 'vfoBMode');
  const vfoAFrequency = readIntInRange(data, 0, CHANNEL_FREQUENCY_MAX, 'vfoAFrequency');
  const vfoBFrequency = readIntInRange(data, 0, CHANNEL_FREQUENCY_MAX, 'vfoBFrequency');
  const txCtcss = readIntInRange(data, 0, ANALOG_CTCSS_MAX, 'txCtcss');
  const rxCtcss = readIntInRange(data, 0, ANALOG_CTCSS_MAX, 'rxCtcss');
  if (
    channel == null
    || vfoAMode == null
    || vfoBMode == null
    || vfoAFrequency == null
    || vfoBFrequency == null
    || txCtcss == null
    || rxCtcss == null
  ) {
    return null;
  }
  const nameRaw = data.name;
  const name = typeof nameRaw === 'string'
    ? parseChannelName(new Uint8Array(encodeChannelName(nameRaw)))
    : '';
  return {
    channel,
    vfoAMode,
    vfoBMode,
    vfoAFrequency,
    vfoBFrequency,
    txCtcss,
    rxCtcss,
    name,
  };
}

export function parsePmr171ChannelEvent(
  deviceId: string,
  type: string,
  data: Record<string, unknown>,
  timestamp: number,
): Pmr171ChannelEvent | null {
  const typeCode = parseFactoryCommandTypeCode(type);
  if (typeCode == null || !isPmr171ChannelType(type)) return null;
  const hex = channelTypeHex(typeCode);
  if (typeCode === PMR171_COMMAND.CHANNEL_MODE) {
    const mode = readIntInRange(data, 0, 1, 'mode');
    if (mode !== 0 && mode !== 1) return null;
    return {
      kind: 'mode',
      deviceId,
      type: hex,
      typeCode,
      timestamp,
      mode,
    };
  }
  const channel = parseAnalogFromData(data);
  if (!channel) return null;
  return {
    kind: 'analog',
    deviceId,
    type: hex,
    typeCode,
    timestamp,
    channel,
  };
}

export function matchesPmr171ChannelEvent(
  event: Pmr171ChannelEvent,
  typeCode: number,
  channel?: number,
): boolean {
  if (event.typeCode !== typeCode) return false;
  if (typeCode === PMR171_COMMAND.CHANNEL_MODE) return event.kind === 'mode';
  return event.kind === 'analog' && event.channel.channel === channel;
}

export function toAnalogChannelWriteData(params: AnalogChannelParams): Record<string, unknown> {
  encodeAnalogChannelWrite(params);
  const name = parseChannelName(new Uint8Array(encodeChannelName(params.name)));
  return {
    channel: params.channel,
    vfoAMode: params.vfoAMode,
    vfoBMode: params.vfoBMode,
    vfoAFrequency: params.vfoAFrequency,
    vfoBFrequency: params.vfoBFrequency,
    txCtcss: params.txCtcss,
    rxCtcss: params.rxCtcss,
    name,
  };
}

export function defaultAnalogChannel(channel: number): AnalogChannelParams {
  return {
    channel,
    vfoAMode: 0,
    vfoBMode: 0,
    vfoAFrequency: 145_000_000,
    vfoBFrequency: 145_000_000,
    txCtcss: 0,
    rxCtcss: 0,
    name: '',
  };
}
